
import { supabase } from '@/integrations/supabase/client';
import { CommentWithVotes, CommentSortOption } from '../types';
import { applySorting } from './helpers';

/**
 * Fetch comments for a card with proper profile joins
 * @param id The card ID (can be empty string when fetching replies by parent_id)
 * @param limit Maximum number of comments to return
 * @param sortBy Sort option (newest, oldest, votes)
 * @param parentId Optional parent ID for fetching replies (null for top-level comments)
 * @returns Array of comments with vote counts
 */
export async function fetchCardComments(
  id: string, 
  limit: number = 50,
  sortBy: CommentSortOption = 'newest',
  parentId: string | null = null
): Promise<CommentWithVotes[]> {
  try {
    console.log(`Fetching ${parentId ? 'replies' : 'comments'} for card ${id || 'n/a'} with limit ${limit}, sorted by ${sortBy}, parent: ${parentId || 'null'}`);
    
    // Build the query WITH the proper profiles join
    let query = supabase
      .from('card_comments')
      .select(`
        *,
        profile:profiles(
          id,
          name,
          avatar_url
        ),
        comment_votes:card_comment_votes(
          id,
          vote_type,
          user_id,
          anonymous_id
        )
      `)
      .is('deleted', false);
    
    // Add filtering based on whether we're fetching top-level comments or replies
    if (parentId === null) {
      // For top-level comments, filter by card_id and null parent_id
      if (id) {
        query = query.eq('card_id', id);
      }
      query = query.is('parent_id', null);
    } else {
      // For replies, filter by parent_id
      query = query.eq('parent_id', parentId);
    }
    
    // Add sorting
    query = applySorting(query, sortBy);
    
    // Add limit to the query
    query = query.limit(limit);
    
    // Execute the query
    const { data, error } = await query;
    
    if (error) throw new Error(error.message);
    
    // Transform the data to include vote counts
    const commentsWithVotes = await Promise.all(
      data.map(async (comment: any) => {
        const upvotes = comment.comment_votes.filter((v: any) => v.vote_type === 'up').length;
        const downvotes = comment.comment_votes.filter((v: any) => v.vote_type === 'down').length;
        
        // Count replies for this comment (only for top-level comments)
        let reply_count = 0;
        if (parentId === null) {
          try {
            const { count, error } = await supabase
              .from('card_comments')
              .select('*', { count: 'exact', head: true })
              .eq('parent_id', comment.id)
              .is('deleted', false);
              
            reply_count = typeof count === 'number' ? count : 0;
            
            if (error) {
              console.error('Error counting replies:', error);
            }
          } catch (err) {
            console.log('Error counting replies:', err);
          }
        }
        
        return {
          ...comment,
          upvotes,
          downvotes,
          reply_count
        };
      })
    );
    
    return commentsWithVotes;
    
  } catch (error) {
    console.error('Error fetching card comments:', error);
    return [];
  }
}
